import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../AuthContext";
import api, { errMsg } from "../api";
import { StickyNote, Plus, Pin, Trash2, Share2, Users, X, Loader2, Pencil, Eraser, Save, Type, Palette, GripVertical, FileDown, ChevronDown, ChevronLeft, FileText, FileSpreadsheet, FileBarChart, Settings as SettingsIcon, RotateCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import NoteRichEditor from "../components/NoteRichEditor";
import { exportNoteToPDF, exportNoteToDOC, exportNoteToXLSX } from "../lib/noteExport";
import VoiceFillButton from "../components/VoiceFillButton";
import LetterheadManager from "../components/LetterheadManager";
import NotePinDialog from "../components/NotePinDialog";
import { Lock, Unlock } from "lucide-react";

const COLORS = [
    { id: "ivory",   bg: "bg-[#FBF7F0]", border: "border-[#E8DFD0]", dot: "bg-[#E8DFD0]", label: "Elfenbein" },
    { id: "stone",   bg: "bg-[#F4F1ED]", border: "border-[#D7D2C9]", dot: "bg-[#A8A29E]", label: "Stein" },
    { id: "sage",    bg: "bg-[#EDF1EC]", border: "border-[#C7D4C3]", dot: "bg-[#84A98C]", label: "Salbei" },
    { id: "rose",    bg: "bg-[#F5ECE9]", border: "border-[#E0CDC5]", dot: "bg-[#C9A99B]", label: "Rosé" },
    { id: "slate",   bg: "bg-[#EEF0F3]", border: "border-[#CCD3DC]", dot: "bg-[#6B7280]", label: "Schiefer" },
    { id: "mauve",   bg: "bg-[#EFEBF1]", border: "border-[#D6CDDD]", dot: "bg-[#9F8CAE]", label: "Mauve" },
];

const PAPER_STYLES = [
    { id: "blank", label: "Blanko" },
    { id: "lines", label: "Liniert" },
    { id: "grid",  label: "Kariert" },
    { id: "dots",  label: "Punkte" },
];
const PAPER_FORMATS = [
    { id: "A4", label: "A4" },
    { id: "A3", label: "A3" },
    { id: "A5", label: "A5" },
    { id: "letter", label: "Letter" },
    { id: "free", label: "Frei" },
];
const ORIENTATIONS = [
    { id: "portrait",  label: "Hoch" },
    { id: "landscape", label: "Quer" },
];

const colorClasses = (c) => COLORS.find((x) => x.id === c) || COLORS[0];

export default function NotesPage() {
    const { user, lang } = useAuth();
    const [notes, setNotes] = useState([]);
    const [active, setActive] = useState(null);
    const [mode, setMode] = useState("text");      // 'text' | 'draw'
    const [shareOpen, setShareOpen] = useState(false);
    const [draggedId, setDraggedId] = useState(null);
    const [letterheads, setLetterheads] = useState([]);
    const [lhManagerOpen, setLhManagerOpen] = useState(false);

    const isManager = user?.role === "admin" || user?.role === "master";

    const load = useCallback(async () => {
        try { const { data } = await api.get("/notes"); setNotes(data || []); } catch (e) { toast.error(errMsg(e, "Error")); }
    }, []);
    const loadLetterheads = useCallback(async () => {
        try { const { data } = await api.get("/letterheads"); setLetterheads(data || []); } catch (e) { /* may be empty */ }
    }, []);
    useEffect(() => { load(); loadLetterheads(); }, [load, loadLetterheads]);

    const createNote = async () => {
        try {
            const { data } = await api.post("/notes", { title: "", content: "", color: "ivory" });
            setActive(data);
            await load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    // Session cache of unlocked notes: noteId -> { dek, html, content, drawing_data }
    const [unlocked, setUnlocked] = useState({});
    const [pinDialogFor, setPinDialogFor] = useState(null); // note object or null

    const saveActive = useCallback(async (changes) => {
        if (!active) return;
        try {
            // Encrypted notes: route plaintext changes through /pin/save with cached DEK.
            if (active.is_encrypted) {
                const unlock = unlocked[active.id];
                if (!unlock?.dek) {
                    // Need to unlock first — present PIN prompt.
                    setPinDialogFor(active);
                    return;
                }
                const plaintextKeys = ["html", "content", "drawing_data", "title"];
                const ptPayload = {};
                const otherPayload = {};
                Object.keys(changes || {}).forEach((k) => {
                    if (plaintextKeys.includes(k)) ptPayload[k] = changes[k];
                    else otherPayload[k] = changes[k];
                });
                if (Object.keys(ptPayload).length > 0) {
                    await api.post(`/notes/${active.id}/pin/save`, { dek: unlock.dek, ...ptPayload });
                }
                if (Object.keys(otherPayload).length > 0) {
                    const { data } = await api.put(`/notes/${active.id}`, otherPayload);
                    setActive((prev) => prev ? { ...prev, ...data, ...ptPayload, ...unlock } : prev);
                } else {
                    setActive((prev) => prev ? { ...prev, ...ptPayload } : prev);
                }
                // Update cache so subsequent renders keep the plaintext.
                if (Object.keys(ptPayload).length > 0) {
                    setUnlocked((prev) => ({ ...prev, [active.id]: { ...prev[active.id], ...ptPayload } }));
                }
                return;
            }
            const { data } = await api.put(`/notes/${active.id}`, changes);
            setActive(data);
            setNotes((prev) => prev.map((n) => n.id === data.id ? data : n));
        } catch (e) { toast.error(errMsg(e, "Speichern fehlgeschlagen")); }
    }, [active, unlocked]);

    const togglePin = async (n) => {
        try { await api.put(`/notes/${n.id}`, { pinned: !n.pinned }); load(); }
        catch (e) { toast.error(errMsg(e, "Error")); }
    };
    const delNote = async (n) => {
        if (!window.confirm(lang === "de" ? "Notiz löschen?" : "Delete note?")) return;
        try { await api.delete(`/notes/${n.id}`); if (active?.id === n.id) setActive(null); load(); }
        catch (e) { toast.error(errMsg(e, "Error")); }
    };

    // Drag & drop reorder (only within owned notes, simple sort)
    const onDrop = async (targetId) => {
        if (!draggedId || draggedId === targetId) return;
        const ownedSorted = notes.filter((n) => n.owner_id === user?.id && !n.pinned);
        const fromIdx = ownedSorted.findIndex((n) => n.id === draggedId);
        const toIdx = ownedSorted.findIndex((n) => n.id === targetId);
        if (fromIdx < 0 || toIdx < 0) return;
        const reordered = [...ownedSorted];
        const [m] = reordered.splice(fromIdx, 1);
        reordered.splice(toIdx, 0, m);
        try {
            await api.post("/notes/reorder", { note_ids: reordered.map((n) => n.id) });
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
        setDraggedId(null);
    };

    const owns = active && active.owner_id === user?.id;

    return (
        <div className="flex h-[calc(100vh-3rem)] bg-neutral-50">
            {/* Sidebar — hidden on mobile when a note is open */}
            <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-72 border-r border-neutral-200 bg-white flex-col`}>
                <div className="px-3 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <StickyNote className="w-4 h-4 text-brand-500" />
                        <span className="font-display text-sm font-bold text-brand-900">Notizblock</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {isManager && (
                            <Button size="sm" variant="outline" onClick={() => setLhManagerOpen(true)} className="h-7 px-2" data-testid="notes-letterhead-manager" title="Briefpapier verwalten">
                                <FileText className="w-3.5 h-3.5" />
                            </Button>
                        )}
                        <Button size="sm" onClick={createNote} className="h-7 bg-brand-500 hover:bg-brand-600 text-white" data-testid="notes-new">
                            <Plus className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="notes-list">
                    {notes.length === 0 ? (
                        <p className="text-xs text-neutral-500 p-4 text-center">{lang === "de" ? "Noch keine Notizen. Klick auf + um deine erste zu erstellen." : "No notes yet."}</p>
                    ) : notes.map((n) => {
                        const c = colorClasses(n.color);
                        const isOwner = n.owner_id === user?.id;
                        return (
                            <div
                                key={n.id}
                                draggable={isOwner && !n.pinned}
                                onDragStart={() => setDraggedId(n.id)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => onDrop(n.id)}
                                onClick={() => {
                                    setActive(n);
                                    if (n.is_encrypted && !unlocked[n.id]) {
                                        setPinDialogFor(n);
                                    }
                                }}
                                data-testid={`note-card-${n.id}`}
                                className={`group cursor-pointer rounded border ${c.bg} ${c.border} p-2 ${active?.id === n.id ? "ring-2 ring-brand-500" : ""}`}
                            >
                                <div className="flex items-start gap-1">
                                    {isOwner && !n.pinned && <GripVertical className="w-3 h-3 text-neutral-400 mt-0.5 shrink-0" />}
                                    {n.pinned && <Pin className="w-3 h-3 text-brand-500 mt-0.5 shrink-0 fill-brand-500" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold truncate">{n.title || (lang === "de" ? "Ohne Titel" : "Untitled")}</p>
                                        <p className="text-[10px] text-neutral-600 line-clamp-2 mt-0.5">{n.content?.slice(0, 80) || (n.drawing_data ? "✏️ Handschrift" : "")}</p>
                                        <div className="flex items-center gap-1 mt-1">
                                            {n.is_encrypted && (
                                                <span className="text-[9px] bg-brand-50 text-brand-700 px-1 rounded flex items-center gap-0.5" title={lang === "de" ? "PIN-geschützt" : "PIN-protected"}>
                                                    <Lock className="w-2 h-2" />
                                                    {n.pin_mode === "individual" ? "PIN/User" : "PIN"}
                                                </span>
                                            )}
                                            {!isOwner && <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">{lang === "de" ? "geteilt" : "shared"}</span>}
                                            {isOwner && (n.shared_with?.length > 0) && <Users className="w-2.5 h-2.5 text-brand-500" title={`${n.shared_with.length} geteilt`} />}
                                        </div>
                                    </div>
                                    {isOwner && (
                                        <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={() => togglePin(n)} className="hover:bg-white/60 rounded p-0.5" data-testid={`note-pin-${n.id}`} title={n.pinned ? "Loslösen" : "Anheften"}>
                                                <Pin className={`w-3 h-3 ${n.pinned ? "fill-brand-500 text-brand-500" : "text-neutral-500"}`} />
                                            </button>
                                            <button onClick={() => delNote(n)} className="hover:bg-red-100 rounded p-0.5" data-testid={`note-del-${n.id}`}>
                                                <Trash2 className="w-3 h-3 text-red-500" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Editor — hidden on mobile when no note open */}
            <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0`}>
                {!active ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 px-8">
                        <StickyNote className="w-12 h-12 text-brand-500/40 mb-3" />
                        <h2 className="font-display text-2xl text-brand-900 mb-1">Notizblock</h2>
                        <p className="text-sm text-center max-w-md">{lang === "de" ? "Schreib oder zeichne deine Notizen. Teile mit Kollegen, hefte wichtige an." : "Write or draw your notes."}</p>
                    </div>
                ) : (
                    <NoteEditor
                        key={active.id}
                        note={active}
                        owns={owns}
                        mode={mode}
                        setMode={setMode}
                        save={saveActive}
                        onShare={() => setShareOpen(true)}
                        onBack={() => setActive(null)}
                        lang={lang}
                        letterheads={letterheads}
                        unlocked={unlocked[active.id]}
                        onPinClick={() => setPinDialogFor(active)}
                    />
                )}
            </div>

            {pinDialogFor && (
                <NotePinDialog
                    note={pinDialogFor}
                    currentUserId={user?.id}
                    lang={lang}
                    onClose={() => setPinDialogFor(null)}
                    onUnlocked={(data) => {
                        setUnlocked((prev) => ({ ...prev, [pinDialogFor.id]: data }));
                        // also push decrypted fields into the active note for the editor
                        if (active?.id === pinDialogFor.id) {
                            setActive((prev) => prev ? { ...prev, html: data.html, content: data.content, drawing_data: data.drawing_data } : prev);
                        }
                    }}
                    onChanged={() => { load(); setUnlocked((prev) => { const c = { ...prev }; delete c[pinDialogFor.id]; return c; }); }}
                />
            )}

            {lhManagerOpen && (
                <LetterheadManager
                    open={lhManagerOpen}
                    onClose={() => setLhManagerOpen(false)}
                    onChanged={loadLetterheads}
                    lang={lang}
                />
            )}

            {shareOpen && active && owns && (
                <ShareNoteDialog
                    note={active}
                    onClose={() => setShareOpen(false)}
                    onUpdated={(n) => { setActive(n); load(); }}
                    lang={lang}
                />
            )}
        </div>
    );
}


function NoteEditor({ note, owns, mode, setMode, save, onShare, onBack, lang, letterheads = [], unlocked, onPinClick }) {
    const [title, setTitle] = useState(note.title || "");
    const [content, setContent] = useState(note.content || unlocked?.content || "");
    const [html, setHtml] = useState(note.html || unlocked?.html || "");
    const [paperStyle, setPaperStyle] = useState(note.paper_style || "blank");
    const [paperFormat, setPaperFormat] = useState(note.paper_format || "A4");
    const [paperOrientation, setPaperOrientation] = useState(note.paper_orientation || "portrait");
    const [letterheadId, setLetterheadId] = useState(note.letterhead_id || "");
    const [saving, setSaving] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const timer = useRef(null);
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef({ x: 0, y: 0 });
    const [penColor, setPenColor] = useState("#131022");
    const [penSize, setPenSize] = useState(2);

    // Debounced auto-save for text/html/title
    useEffect(() => {
        setTitle(note.title || "");
        setContent(note.content || unlocked?.content || "");
        setHtml(note.html || unlocked?.html || "");
        setPaperStyle(note.paper_style || "blank");
        setPaperFormat(note.paper_format || "A4");
        setPaperOrientation(note.paper_orientation || "portrait");
        setLetterheadId(note.letterhead_id || "");
    }, [note.id, note.title, note.content, note.html, note.paper_style, note.paper_format, note.paper_orientation, note.letterhead_id, unlocked?.html, unlocked?.content]);
    useEffect(() => {
        if (mode !== "text") return;
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            const changed =
                title !== (note.title || "") ||
                content !== (note.content || "") ||
                html !== (note.html || "");
            if (changed) {
                setSaving(true);
                await save({ title, content, html });
                setSaving(false);
            }
        }, 800);
        return () => clearTimeout(timer.current);
    }, [title, content, html, mode, save, note.title, note.content, note.html]);

    const changeStyle = (s) => { setPaperStyle(s); save({ paper_style: s }); };
    const changeFormat = (f) => { setPaperFormat(f); save({ paper_format: f }); };
    const changeOrientation = (o) => { setPaperOrientation(o); save({ paper_orientation: o }); };
    const changeLetterhead = (lid) => { setLetterheadId(lid); save({ letterhead_id: lid || null }); };

    const activeLetterhead = letterheads.find((l) => l.id === letterheadId) || null;
    const letterheadUrl = activeLetterhead
        ? `${process.env.REACT_APP_BACKEND_URL}/api/letterheads/${activeLetterhead.id}/image`
        : null;

    const doExport = async (kind) => {
        setExportOpen(false);
        try {
            if (kind === "pdf") await exportNoteToPDF({ title, html, paper_format: paperFormat, paper_orientation: paperOrientation, letterhead_url: letterheadUrl });
            else if (kind === "doc") exportNoteToDOC({ title, html, paper_format: paperFormat, paper_orientation: paperOrientation, letterhead_url: letterheadUrl });
            else if (kind === "xlsx") exportNoteToXLSX({ title, html });
            toast.success(lang === "de" ? "Export bereit" : "Export ready");
        } catch (e) {
            toast.error(`Export ${kind} fehlgeschlagen`);
            console.error(e);
        }
    };

    // Init canvas with existing drawing
    useEffect(() => {
        if (mode !== "draw") return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (note.drawing_data) {
            const img = new Image();
            img.onload = () => ctx.drawImage(img, 0, 0);
            img.src = note.drawing_data;
        }
    }, [mode, note.drawing_data]);

    const startDraw = (e) => {
        if (!owns) return;
        drawingRef.current = true;
        const rect = canvasRef.current.getBoundingClientRect();
        const p = e.touches?.[0] || e;
        lastRef.current = { x: p.clientX - rect.left, y: p.clientY - rect.top };
    };
    const doDraw = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const rect = canvasRef.current.getBoundingClientRect();
        const p = e.touches?.[0] || e;
        const x = p.clientX - rect.left;
        const y = p.clientY - rect.top;
        ctx.strokeStyle = penColor;
        ctx.lineWidth = penSize;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        ctx.moveTo(lastRef.current.x, lastRef.current.y);
        ctx.lineTo(x, y);
        ctx.stroke();
        lastRef.current = { x, y };
    };
    const endDraw = () => { drawingRef.current = false; };

    const saveDrawing = async () => {
        const dataUrl = canvasRef.current.toDataURL("image/png");
        setSaving(true);
        await save({ drawing_data: dataUrl });
        setSaving(false);
        toast.success(lang === "de" ? "Zeichnung gespeichert" : "Drawing saved");
    };
    const clearDrawing = () => {
        const ctx = canvasRef.current.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    };

    const c = colorClasses(note.color);

    return (
        <>
            <header className="bg-white border-b border-neutral-200 px-3 sm:px-6 py-2 sm:py-3 flex items-center gap-2 sm:gap-3 flex-wrap">
                <button
                    onClick={onBack}
                    className="md:hidden p-1.5 -ml-1 rounded hover:bg-neutral-100"
                    data-testid="note-back-btn"
                    aria-label="Zurück"
                    type="button"
                >
                    <ChevronLeft className="w-5 h-5 text-neutral-700" />
                </button>
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={lang === "de" ? "Titel" : "Title"}
                    disabled={!owns}
                    className="font-display text-lg sm:text-xl flex-1 min-w-0 bg-transparent border-0 outline-none focus:ring-0 text-brand-900 placeholder:text-brand-900/30 tracking-tight"
                    data-testid="note-title-input"
                />
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />}
                {/* Mode toggle */}
                <div className="flex items-center gap-0.5 bg-neutral-100 rounded-md p-0.5">
                    <button onClick={() => setMode("text")} className={`px-2.5 py-1 rounded-sm text-[11px] font-medium flex items-center gap-1 transition ${mode === "text" ? "bg-white shadow-sm text-brand-900" : "text-neutral-500 hover:text-neutral-700"}`} data-testid="note-mode-text" type="button">
                        <Type className="w-3 h-3" /> Text
                    </button>
                    <button onClick={() => setMode("draw")} className={`px-2.5 py-1 rounded-sm text-[11px] font-medium flex items-center gap-1 transition ${mode === "draw" ? "bg-white shadow-sm text-brand-900" : "text-neutral-500 hover:text-neutral-700"}`} data-testid="note-mode-draw" type="button">
                        <Pencil className="w-3 h-3" /> Zeichnen
                    </button>
                </div>
                {/* Paper format & style — only in text mode */}
                {mode === "text" && owns && (
                    <>
                        <select
                            value={paperFormat}
                            onChange={(e) => changeFormat(e.target.value)}
                            className="h-7 text-[11px] border border-neutral-200 rounded px-1.5 bg-white"
                            title={lang === "de" ? "Papierformat" : "Paper format"}
                            data-testid="note-format-select"
                        >
                            {PAPER_FORMATS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                        <select
                            value={paperOrientation}
                            onChange={(e) => changeOrientation(e.target.value)}
                            className="h-7 text-[11px] border border-neutral-200 rounded px-1.5 bg-white"
                            title={lang === "de" ? "Ausrichtung" : "Orientation"}
                            data-testid="note-orientation-select"
                        >
                            {ORIENTATIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </select>
                        <select
                            value={paperStyle}
                            onChange={(e) => changeStyle(e.target.value)}
                            className="h-7 text-[11px] border border-neutral-200 rounded px-1.5 bg-white"
                            title={lang === "de" ? "Papierstil" : "Paper style"}
                            data-testid="note-style-select"
                        >
                            {PAPER_STYLES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                        {letterheads.length > 0 && (
                            <select
                                value={letterheadId}
                                onChange={(e) => changeLetterhead(e.target.value)}
                                className="h-7 text-[11px] border border-neutral-200 rounded px-1.5 bg-white max-w-[140px]"
                                title={lang === "de" ? "Firmenbriefpapier" : "Letterhead"}
                                data-testid="note-letterhead-select"
                            >
                                <option value="">— {lang === "de" ? "Kein Briefpapier" : "No letterhead"} —</option>
                                {letterheads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        )}
                    </>
                )}
                {owns && (
                    <>
                        <div className="hidden sm:flex items-center gap-1 px-1 border-l border-r border-neutral-200 mx-1">
                            {COLORS.map((col) => (
                                <button
                                    key={col.id}
                                    onClick={() => save({ color: col.id })}
                                    className={`w-4 h-4 rounded-full ${col.dot} transition-all ${note.color === col.id ? "ring-2 ring-offset-1 ring-brand-500 scale-110" : "ring-1 ring-neutral-300 hover:scale-105"}`}
                                    data-testid={`note-color-${col.id}`}
                                    title={col.label}
                                    type="button"
                                />
                            ))}
                        </div>
                        {/* Export */}
                        <div className="relative">
                            <Button size="sm" variant="outline" onClick={() => setExportOpen((o) => !o)} className="h-7 gap-1" data-testid="note-export-btn" type="button">
                                <FileDown className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">{lang === "de" ? "Export" : "Export"}</span>
                                <ChevronDown className="w-3 h-3" />
                            </Button>
                            {exportOpen && (
                                <div className="absolute right-0 top-9 z-30 bg-white border border-neutral-200 rounded-md shadow-lg w-40 py-1 text-sm" data-testid="note-export-menu">
                                    <button onClick={() => doExport("pdf")} className="w-full text-left px-3 py-1.5 hover:bg-neutral-50 flex items-center gap-2" data-testid="note-export-pdf" type="button">
                                        <FileText className="w-3.5 h-3.5 text-red-600" /> PDF
                                    </button>
                                    <button onClick={() => doExport("doc")} className="w-full text-left px-3 py-1.5 hover:bg-neutral-50 flex items-center gap-2" data-testid="note-export-doc" type="button">
                                        <FileBarChart className="w-3.5 h-3.5 text-blue-600" /> Word (.doc)
                                    </button>
                                    <button onClick={() => doExport("xlsx")} className="w-full text-left px-3 py-1.5 hover:bg-neutral-50 flex items-center gap-2" data-testid="note-export-xlsx" type="button">
                                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" /> Excel (.xlsx)
                                    </button>
                                </div>
                            )}
                        </div>
                        <Button size="sm" variant="outline" onClick={onShare} className="h-7 gap-1" data-testid="note-share-btn" type="button">
                            <Share2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{note.shared_with?.length ? note.shared_with.length : (lang === "de" ? "Teilen" : "Share")}</span>
                        </Button>
                        {owns && (
                            <Button size="sm" variant="outline" onClick={onPinClick} className={`h-7 gap-1 ${note.is_encrypted ? "border-brand-500 text-brand-500" : ""}`} data-testid="note-pin-btn" type="button" title={lang === "de" ? "PIN-Schutz" : "PIN protection"}>
                                {note.is_encrypted ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                                <span className="hidden sm:inline">{note.is_encrypted ? "PIN" : (lang === "de" ? "PIN setzen" : "Set PIN")}</span>
                            </Button>
                        )}
                        <VoiceFillButton
                            formType="note"
                            lang={lang}
                            label={lang === "de" ? "Sprechen" : "Speak"}
                            onParsed={(f) => {
                                if (f.title) setTitle(String(f.title));
                                if (f.content) {
                                    // Convert plain markdown-ish content to simple HTML for the rich editor.
                                    const txt = String(f.content);
                                    const htmlContent = txt
                                        .split(/\n\n+/)
                                        .map((para) => `<p>${para.replace(/\n/g, "<br/>")}</p>`)
                                        .join("");
                                    setHtml(htmlContent);
                                    setContent(txt);
                                    save({ title: f.title || title, content: txt, html: htmlContent });
                                }
                            }}
                        />
                    </>
                )}
            </header>

            <div className={`flex-1 overflow-auto ${letterheadUrl ? "bg-neutral-100" : c.bg}`}>
                {mode === "text" ? (
                    <div
                        className={`note-paper note-paper-${paperFormat} note-style-${paperStyle} ${paperOrientation === "landscape" ? "landscape" : ""} ${letterheadUrl ? "has-letterhead" : ""}`}
                        style={letterheadUrl ? { "--letterhead-url": `url('${letterheadUrl}')` } : undefined}
                        data-testid="note-paper"
                    >
                        {note.is_encrypted && !unlocked ? (
                            <div className="p-12 flex flex-col items-center justify-center text-center" data-testid="note-locked-state">
                                <Lock className="w-12 h-12 text-brand-500/60 mb-4" />
                                <h3 className="font-display text-2xl text-brand-900 mb-2">{lang === "de" ? "Diese Notiz ist PIN-geschützt" : "This note is PIN-protected"}</h3>
                                <p className="text-sm text-neutral-500 mb-6 max-w-md">{lang === "de" ? "Gib deine PIN ein, um den Inhalt zu sehen." : "Enter your PIN to view the content."}</p>
                                <Button onClick={onPinClick} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="note-unlock-cta">
                                    <Unlock className="w-3.5 h-3.5 mr-1.5" /> {lang === "de" ? "Entsperren" : "Unlock"}
                                </Button>
                            </div>
                        ) : (
                            <NoteRichEditor
                                value={html}
                                onChange={({ html: h, text }) => { setHtml(h); setContent(text); }}
                                readOnly={!owns && note.owner_id !== undefined}
                                lang={lang}
                            />
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-3 p-4 sm:p-8">
                        <div className="bg-white/90 backdrop-blur rounded-md shadow-sm border border-neutral-200/50 flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs">
                            <Palette className="w-3.5 h-3.5 text-neutral-400" />
                            {["#131022", "#6B4EFF", "#9F1239", "#166534", "#1E40AF", "#A16207"].map((col) => (
                                <button key={col} onClick={() => setPenColor(col)} className={`w-4 h-4 rounded-full transition-all ${penColor === col ? "ring-2 ring-offset-1 ring-brand-500 scale-110" : "hover:scale-110"}`} style={{ background: col }} />
                            ))}
                            <span className="text-neutral-300">|</span>
                            <input type="range" min="1" max="10" value={penSize} onChange={(e) => setPenSize(parseInt(e.target.value, 10))} className="w-20 accent-brand-500" />
                            <span className="text-neutral-500 font-mono tabular-nums w-7">{penSize}px</span>
                            <button onClick={clearDrawing} className="ml-2 px-2.5 py-1 rounded-md text-neutral-600 hover:bg-neutral-100 flex items-center gap-1 transition" data-testid="note-clear">
                                <Eraser className="w-3 h-3" /> Löschen
                            </button>
                            <button onClick={saveDrawing} className="px-2.5 py-1 rounded-md bg-brand-900 hover:bg-brand-800 text-white flex items-center gap-1 transition" data-testid="note-save-drawing">
                                <Save className="w-3 h-3" /> Speichern
                            </button>
                        </div>
                        <canvas
                            ref={canvasRef}
                            width={900}
                            height={600}
                            onMouseDown={startDraw}
                            onMouseMove={doDraw}
                            onMouseUp={endDraw}
                            onMouseLeave={endDraw}
                            onTouchStart={startDraw}
                            onTouchMove={doDraw}
                            onTouchEnd={endDraw}
                            className="bg-white rounded-md shadow-[0_8px_30px_rgb(0,0,0,0.08)] cursor-crosshair touch-none border border-neutral-200/40"
                            data-testid="note-canvas"
                        />
                    </div>
                )}
            </div>
        </>
    );
}


function ShareNoteDialog({ note, onClose, onUpdated, lang }) {
    const [users, setUsers] = useState([]);
    const [sharedWith, setSharedWith] = useState(note.shared_with || []);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role !== "deactivated"))).catch(() => {});
    }, []);
    const toggle = (uid) => setSharedWith((p) => p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]);
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/notes/${note.id}/share`, { user_ids: sharedWith });
            onUpdated(data);
            toast.success(lang === "de" ? "Freigabe aktualisiert" : "Sharing updated");
            onClose();
        } catch (e) { toast.error(errMsg(e, "Error")); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="note-share-dialog">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <p className="font-semibold">{lang === "de" ? "Notiz teilen" : "Share note"}</p>
                    <button onClick={onClose}><X className="w-4 h-4" /></button>
                </header>
                <div className="p-4 max-h-[60vh] overflow-y-auto">
                    <p className="text-xs text-neutral-500 mb-2">{lang === "de" ? "Wähle Kollegen, die diese Notiz lesen UND bearbeiten dürfen:" : "Pick colleagues who can read AND edit:"}</p>
                    <div className="space-y-1">
                        {users.map((u) => (
                            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-neutral-50 rounded cursor-pointer">
                                <input type="checkbox" checked={sharedWith.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`note-share-${u.id}`} />
                                <span className="text-sm">{u.name}</span>
                                <span className="text-[10px] text-neutral-400 ml-auto">{u.email}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="note-share-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "de" ? "Speichern" : "Save")}
                    </Button>
                </footer>
            </div>
        </div>
    );
}
